import { WSFormProps, WSList, WSText } from "@wingspanhq/fe-component-library";
import {
  GetMissingFieldsLevelParameter,
  VerificationLevel
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { queriesOnboarding, queriesPersons } from "@query";
import { useSession } from "@hooks/useSession";
import { verificationService } from "../../../../shared/services/verificaiton";
import { CardTaxVerificationRequirementRequester } from "../../components/CardTaxVerificationRequirementRequester";
import { getProfileDataPersonV3 } from "../../components/FormPartialPerson/getProfileDataPersonV3";
import {
  FormDataPersonalInformation,
  FormPersonalInformation
} from "../../components/FormPersonalInformation";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { OnboardingModuleCreateAccount } from "../../types";
import { CreateAccountContext } from "./types";
import { useRedirectToAccountType } from "./useRedirectToAccountType";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

export const PersonalInformation: React.FC<FlowStep> = ({ onNext, onBack }) => {
  const [context, setContext] = useOnboardingContext<CreateAccountContext>();

  const session = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery({
    personId: session.personId
  });
  const queryOnboardingStatus = queriesOnboarding.getOnboardingStatus.useQuery(
    {}
  );

  const profileData: WSFormProps<FormDataPersonalInformation>["defaultValues"] =
    useMemo(
      () => ({
        person: getProfileDataPersonV3({
          type: "Individual",
          person: queryPerson.data
        })
      }),
      [queryPerson.data]
    );

  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;
  const verificationLevel =
    onboardingModule?.options?.verificationLevel || "Tax";

  const queryMissingData = queriesOnboarding.getMissingFields.useQuery({
    level: verificationLevel as GetMissingFieldsLevelParameter
  });

  const isMismatch = useMemo(
    () =>
      verificationService.getIsIndividualMismatch(
        queryMissingData.data?.requiredFields || []
      ),
    [queryMissingData.data?.requiredFields]
  );

  const isCompleted = useMemo(() => {
    const verifications = queryOnboardingStatus.data?.verifications;
    const status =
      verificationLevel === "Banking"
        ? verifications?.banking
        : verifications?.tax;

    return status === VerificationLevel.VERIFIED;
  }, [queryOnboardingStatus.data, verificationLevel]);

  useRedirectToAccountType();

  return (
    <WSList gap="2XL">
      <CardTaxVerificationRequirementRequester />

      <FormPersonalInformation
        defaultValues={context.personalInformation || profileData}
        onBack={onBack}
        onSubmit={data => {
          isCompleted ||
            setContext(prev => ({
              ...prev,
              personalInformation: data
            }));
          onNext?.();
        }}
        mismatch={isMismatch}
        country={context.accountType?.country || "US"}
        completed={isCompleted}
      />

      <WSText.ParagraphXs color="gray400">
        The information on this page is required to setup your Wingspan account,
        if you exit or leave without completing this step, you will return to
        this page the next time you sign-in
      </WSText.ParagraphXs>
    </WSList>
  );
};
